import { useEventTicketsBonusMediaLazy } from "hooks/event/useEventTicketsBonusMediaLazy";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "utility/useQuery";
import { z } from "zod/mini";

import { getEventIdByGetTicketId } from "./utils/getEventIdByGetTicketId";

type Input = {
  parameters: {
    orderId: string;
    lineItemId: string;
  };
};

const eventTicketBonusMediaSchema = z.object({
  eventId: z.string(),
  ticketId: z.string(),
  mediaId: z.string(),
  filename: z.string(),
  bonusName: z.string(),
  releaseAt: z.optional(z.date()),
  expiredAt: z.optional(z.date()),
  urlForDownload: z.optional(z.string()),
});

const useEventTicketsBonusMediaSchema = z.object({
  data: z.optional(z.array(eventTicketBonusMediaSchema)),
  isMutating: z.boolean(),
  trigger: z.function(),
  error: z.optional(z.nullable(z.instanceof(Error))),
});

export const useThis = (input: Input) => {
  const {
    parameters: { orderId, lineItemId },
  } = input;

  const query = useQuery();
  const eventIdFromQuery = query.get("events");
  const [eventId, setEventId] = useState<string | null>(eventIdFromQuery);
  const [ticketError, setTicketError] = useState<Error | null>(null);
  const [parseError, setParseError] = useState<Error | null>(null);

  const useEventTicketsBonusMediaLazyReturn = useEventTicketsBonusMediaLazy(
    eventId ?? "",
    [lineItemId]
  );
  const parseResult = useEventTicketsBonusMediaSchema.safeParse(
    useEventTicketsBonusMediaLazyReturn
  );
  const { data, isMutating, trigger, error } = parseResult.success
    ? parseResult.data
    : {
        data: undefined,
        isMutating: false,
        trigger: async () => Promise.resolve(),
        error: null,
      };
  const triggerRef = useRef(trigger);
  triggerRef.current = trigger;

  useEffect(() => {
    if (parseResult.success === false) {
      setParseError(
        new Error(`Failed to parse hook return: ${parseResult.error.message}`)
      );
    } else {
      setParseError(null);
    }
  }, [parseResult.success, parseResult.error?.message]);

  useEffect(() => {
    if (!eventIdFromQuery && orderId && lineItemId) {
      void getEventIdByGetTicketId(orderId, lineItemId)
        .then((retrievedEventId) => {
          setEventId(retrievedEventId);
          setTicketError(null);
        })
        .catch((err) => {
          setTicketError(err instanceof Error ? err : new Error(String(err)));
        });
    }
  }, [eventIdFromQuery, orderId, lineItemId]);

  useEffect(() => {
    if (eventId) {
      void triggerRef.current();
    }
  }, [eventId]);

  const showingError = ticketError ?? (eventId && error ? error : parseError);
  const showLoading = isMutating || !eventId;

  return {
    orderId,
    lineItemId,
    bonusMediaList: data ?? [],
    isLoading: showLoading,
    error: showingError,
  };
};
